'use client';

import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import DetailsNeededPrompt from './DetailsNeededPrompt';
import { useEnrollmentDetails } from './useEnrollmentDetails';

interface DetailsNeededBannerProps {
  enrollmentId: string;
  variant?: 'inline' | 'compact';
}

/** Tells the participant the ticket needs more details; nothing when complete or when it cannot be known. */
export default function DetailsNeededBanner({ enrollmentId, variant = 'inline' }: DetailsNeededBannerProps) {
  const { t } = useLanguage();
  const { data } = useEnrollmentDetails(enrollmentId);
  const [open, setOpen] = useState(false);

  if (!data) return null;

  const prompt = <DetailsNeededPrompt enrollmentId={enrollmentId} open={open} onClose={() => setOpen(false)} />;

  if (variant === 'compact') {
    return (
      <>
        <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
          <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          <p className="flex-1 text-xs font-medium text-amber-900">{t('details.bannerTitle')}</p>
          <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(true)}>{t('details.fillIn')}</Button>
        </div>
        {prompt}
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg bg-amber-50 border border-amber-200 p-4">
        <AlertCircle className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-900">{t('details.bannerTitle')}</p>
          <p className="text-sm text-amber-900">{t('details.bannerBody')}</p>
        </div>
        <Button type="button" variant="primary" onClick={() => setOpen(true)}>{t('details.fillIn')}</Button>
      </div>
      {prompt}
    </>
  );
}
